import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import Modal from 'react-responsive-modal'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'
import { createHolidays, updateHolidays } from '../../actions/holidays'
import { Input } from '../../components/ModalInputs'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import styles from './styles.module.scss'

const INITIAL_VALUES = {
  name: '',
  date: moment().format('YYYY-MM-DD'),
  text: '',
  timestamp: ''
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Обязательное поле')
    .max(30, 'Должно быть не более 30 символов'),
  date: Yup.string()
    .required('Обязательное поле'),
})

const HolidaysModal = ({ open, onClose, data, addMode }) => {
  const dispatch = useDispatch()
  const [confirmation, setConfirmation] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createItem = useCallback(async (values) => {
    setIsSubmitting(true)
    try {
      await dispatch(createHolidays(values, onClose))
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, onClose])

  const updateItem = useCallback(async (values) => {
    setIsSubmitting(true)
    try {
      await dispatch(updateHolidays(values, onClose))
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, onClose])

  const {
    values,
    errors,
    touched,
    dirty,
    handleBlur,
    handleSubmit,
    handleChange,
    setValues,
    resetForm
  } = useFormik({
    initialValues: INITIAL_VALUES,
    enableReinitialize: true,
    validationSchema,
    onSubmit: addMode ? createItem : updateItem
  })

  useEffect(() => {
    if (!open) {
      resetForm()
      return
    }
    if (addMode) {
      setValues(INITIAL_VALUES)
    } else if (data) {
      setValues({
        ...data,
        date: data.date ? moment(data.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        timestamp: data.timestamp ? moment(data.timestamp).format('YYYY-MM-DD') : ''
      })
    }
  }, [open, addMode, data, setValues, resetForm])

  const handleCloseModal = useCallback(() => {
    if (dirty) {
      setConfirmation(true)
    } else {
      onClose()
    }
  }, [dirty, onClose])

  const acceptConfirmation = useCallback(() => {
    onClose()
    setConfirmation(false)
  }, [onClose])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(false)
  }, [])

  const getError = useCallback((fieldName) =>
    touched[fieldName] && errors[fieldName],
    [touched, errors]
  )

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      classNames={{ modal: styles.modal }}
      closeOnEsc={!dirty}
      closeOnOverlayClick={!dirty}
      showCloseIcon={false}
      animationDuration={200}
      center
    >
      <form onSubmit={handleSubmit} className={styles.modalForm}>
        <div className={styles.modalMain}>
          <h2>{addMode ? 'Добавление праздничного дня' : 'Редактирование праздничного дня'}</h2>

          <Input
            type="date"
            name="date"
            label="Дата"
            value={values.date || ''}
            error={getError('date')}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
          />

          <Input
            name="name"
            label="Название"
            value={values.name || ''}
            error={getError('name')}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
            autoFocus
          />

          <Input
            type="date"
            name="timestamp"
            label="Перенос на"
            value={values.timestamp || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
          />

          <Input
            name="text"
            label="Примечание"
            value={values.text || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.footerInfo} />
          <div className={styles.footerButtons}>
            <Button
              mode="white"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting || !dirty}
            >
              Сохранить
            </Button>
            <Button
              mode="white"
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Закрыть
            </Button>
          </div>
        </div>
      </form>

      {confirmation && (
        <Confirmation
          title="Есть несохраненные данные. Уверены что хотите выйти?"
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
        />
      )}
    </Modal>
  )
}

export default HolidaysModal
